import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import ActiveDirectoryConfiguration from "./components/ActiveDirectoryConfiguration";
import GeneralConfiguration from "./components/GeneralConfiguration";
import {Box, Tab, Tabs, Typography,} from '@mui/material';
import {Header, StyledContainer} from './Configuration.styled';
import ActiveDirectoryGroupsConfiguration from "./components/ActiveDirectoryGroupsConfiguration";
import SharePointConfiguration from "./components/SharePointConfiguration";
import {useFeatureConfig} from "../../../contexts/FeatureConfigContext";
import {FEATURE_KEYS} from "../../../models/admin-config.models";

const TAB_KEYS = {
    GENERAL: 'general',
    AD: 'ad',
    AD_GROUPS: 'ad-groups',
    SHAREPOINT: 'sharepoint',
} as const;

type TabKey = typeof TAB_KEYS[keyof typeof TAB_KEYS];

const Configuration = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<TabKey>(TAB_KEYS.GENERAL);
    const {isFeatureEnabled} = useFeatureConfig();

    const tabsConfig = [
        {
            id: "general-configuration",
            key: TAB_KEYS.GENERAL,
            labelKey: 'configuration.tabs.generalSettings',
            visible: true },
        {
            id: "active-directory-configuration",
            key: TAB_KEYS.AD,
            labelKey: 'configuration.tabs.activeDirectoryConfig',
            visible: isFeatureEnabled(FEATURE_KEYS.ACTIVE_DIRECTORY_KEY)
        },
        {
            id: "active-directory-groups-configuration",
            key: TAB_KEYS.AD_GROUPS,
            labelKey: 'configuration.tabs.activeDirectoryGroupsConfig',
            visible: isFeatureEnabled(FEATURE_KEYS.ACTIVE_DIRECTORY_KEY)
        },
        {
            id: "sharepoint-configuration",
            key: TAB_KEYS.SHAREPOINT,
            labelKey: 'configuration.tabs.sharePointConfig',
            visible: isFeatureEnabled(FEATURE_KEYS.SHAREPOINT_KEY)
        },
    ];

    const visibleTabs = tabsConfig.filter(tab => tab.visible);

    const handleTabChange = (_: React.SyntheticEvent, newValue: TabKey) => {
        setActiveTab(newValue);
    };

    return (
        <StyledContainer role="region" aria-labelledby="configuration-title">
            <Header>
                <Typography id="configuration-title" variant="h4" component="h2">
                    {t('configuration.title')}
                </Typography>
            </Header>

            <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                aria-live="polite"
                aria-atomic={true}
                role="group"
                aria-label={t('configuration.settingsGroupLabel') as string}
            >
                <Box sx={{borderBottom: 'none !important'}}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label={t('configuration.tabs.ariaLabel') as string}
                    >
                        {visibleTabs.map((tab) => (
                            <Tab
                                id={`${tab.id}-tab`}
                                key={tab.key}
                                value={tab.key}
                                label={t(tab.labelKey)}
                                aria-controls={`${tab.id}-panel`}
                                sx={{textTransform: 'none'}}
                            />
                        ))}
                    </Tabs>
                </Box>

                {activeTab === TAB_KEYS.GENERAL && (
                    <Box role="tabpanel" id="general-configuration-panel"
                         aria-labelledby="general-configuration-tab"
                         aria-label={t('configuration.tabs.generalSettingsAria') as string}
                    >
                        <GeneralConfiguration />
                    </Box>
                )}
                {(activeTab === TAB_KEYS.AD && isFeatureEnabled(FEATURE_KEYS.ACTIVE_DIRECTORY_KEY)) && (
                    <Box role="tabpanel" id="active-directory-configuration-panel"
                         aria-labelledby="active-directory-configuration-tab"
                         aria-label={t('configuration.tabs.activeDirectoryConfigAria') as string}
                    >
                        <ActiveDirectoryConfiguration />
                    </Box>
                )}
                {(activeTab === TAB_KEYS.AD_GROUPS && isFeatureEnabled(FEATURE_KEYS.ACTIVE_DIRECTORY_KEY)) && (
                    <Box role="tabpanel"
                         id="active-directory-groups-configuration-panel"
                         aria-labelledby="active-directory-groups-configuration-tab"
                         aria-label={t('configuration.tabs.activeDirectoryGroupsConfigAria') as string}
                    >
                        <ActiveDirectoryGroupsConfiguration />
                    </Box>
                )}
                {(activeTab === TAB_KEYS.SHAREPOINT && isFeatureEnabled(FEATURE_KEYS.SHAREPOINT_KEY)) && (
                    <Box role="tabpanel"
                         id="sharepoint-configuration-panel"
                         aria-labelledby="sharepoint-configuration-tab"
                         aria-label={t('configuration.tabs.sharePointConfigAria') as string}
                    >
                        <SharePointConfiguration />
                    </Box>
                )}
            </Box>
        </StyledContainer>
    );
};

export default Configuration;
